import { useCallback } from "react";
import { ERR_MSG } from "./const";
import type { ErrorCodeType } from "./type";
import { URL } from "~/components/blocks/layout/global-navi/const/url";
import { runningInBrowser } from "~/const/browser";
import { NOT_REMOVE_COOKIE } from "~/const/cookie";
import { graphQLErrorsVar, networkErrorVar } from "~/lib/apollo/link";
import { useAuth } from "~/provider/auth";
import { autoLoginScript } from "~/util/auto-login-script";
import { getCookie } from "~/util/get-cookie";
import { uriConverter } from "~/util/uri-converter";

const REMOVE_LOGIN_SESSION_ID =
  "login_session_id=; expires=session; max-age=session; secure=false; path=/; domain=.dmm.com;";

export const useErrorDialog = () => {
  const { execAuth } = useAuth();

  const execLogin = useCallback(() => {
    const backUrl = uriConverter.encode(location.href);
    const loginUrl = `${URL.login}${backUrl}`;
    location.href = loginUrl;
  }, []);

  const resetError = useCallback(() => {
    graphQLErrorsVar(undefined);
    networkErrorVar(undefined);
  }, []);

  const resetErrorAndLogout = useCallback(() => {
    if (!runningInBrowser) return;

    resetError();

    const isAutoLogin = !!getCookie(NOT_REMOVE_COOKIE.HAS_ALT_HASH.KEY);

    // MEMO（kimura）: Cookieを消すためには設定した属性をすべて書かなければ消えない場合がある
    // MEMO（kimura）: なので設定した属性をすべて記載した上で `login_session_id=""` の状態に変化させている
    document.cookie = REMOVE_LOGIN_SESSION_ID;

    if (isAutoLogin) {
      autoLoginScript();
      return;
    }

    execLogin();
  }, []);

  const resetErrorAndExecAuth = useCallback(() => {
    resetError();
    execAuth();
  }, []);

  const errorHandling = (code: ErrorCodeType) => {
    switch (code) {
      case "NOT_FOUND":
        return { description: ERR_MSG.COMMON.NOT_FOUND, action: () => resetError() };
      case "UNAUTHORIZED":
        return { description: ERR_MSG.COMMON.UNAUTHORIZED, action: () => resetErrorAndLogout() };
      case "PROFILE_UNSPECIFIED":
        return { description: ERR_MSG.COMMON.PROFILE_UNSPECIFIED, action: () => resetErrorAndExecAuth() };
      case "ALREADY_EXISTS":
        return { description: ERR_MSG.COMMON.ALREADY_EXISTS, action: () => resetError() };
      case "INVALID_ARGUMENT":
        return { description: ERR_MSG.COMMON.INVALID_ARGUMENT, action: () => resetError() };
      case "INTERNAL_ERROR":
        return { description: ERR_MSG.COMMON.INTERNAL_ERROR, action: () => resetError() };
      case "UNSPECIFIED":
        return { description: ERR_MSG.POST_REVIEW.UNSPECIFIED, action: () => resetError() };
      case "INVALID_TITLE":
        return { description: ERR_MSG.POST_REVIEW.INVALID_TITLE, action: () => resetError() };
      case "INVALID_COMMENT":
        return { description: ERR_MSG.POST_REVIEW.INVALID_COMMENT, action: () => resetError() };
      case "NO_REVIEWER_NAME":
        return { description: ERR_MSG.POST_REVIEW.NO_REVIEWER_NAME, action: () => resetError() };
      case "UNAVAILABLE_REVIEWER_NAME":
        return { description: ERR_MSG.POST_REVIEW.UNAVAILABLE_REVIEWER_NAME, action: () => resetError() };
      case "REVIEWER_NAME_IS_REGISTERED":
        return { description: ERR_MSG.POST_REVIEW.REVIEWER_NAME_IS_REGISTERED, action: () => resetError() };
      case "INVALID_REVIEWER_NAME":
        return { description: ERR_MSG.POST_REVIEW.INVALID_REVIEWER_NAME, action: () => resetError() };
      case "USER_NOT_FOUND":
        return { description: ERR_MSG.PROFILE.USER_NOT_FOUND, action: () => resetError() };
      case "PROFILE_NOT_FOUND":
        return { description: ERR_MSG.PROFILE.PROFILE_NOT_FOUND, action: () => resetError() };
      case "PROFILE_INACTIVE":
        return { description: ERR_MSG.PROFILE.PROFILE_INACTIVE, action: () => resetError() };
      case "DEFAULT_PROFILE_CANNOT_DELETE":
        return { description: ERR_MSG.PROFILE.DEFAULT_PROFILE_CANNOT_DELETE, action: () => resetError() };
      // memo: BFF側でエラーコードが追加されてFEで検知できなかった場合を考慮しておく
      default:
        return { description: ERR_MSG.COMMON.DEFAULT, action: () => resetError() };
    }
  };

  return {
    resetError,
    errorHandling,
  };
};
